import React, { useState } from 'react';

import { Button, Typography } from '@pkg/components';
import { Wrapper } from '@pkg/components/DesignSystem';
import { StyledForm } from '@pkg/components/Sections/Admin/components/Form/styles';
import { useAppDispatch } from '@pkg/hooks';

import { apiFactory } from '@app/client/apiFactory/api';
import { type Guarantee, type LootBoxType, type Weight } from '@app/client/common/structs';
import { Admin } from '@app/client/resources';
import { addNotification } from '@app/store/actions/notification';

import ConfigInput from './ConfigInput';
import GuaranteeConfig from './GuaranteeConfig';
import WeightConfig from './WeightConfig';
import './_LootBoxTypeConfig.scss';

interface LootBoxTypeConfigProps {
    onTypeSaved: () => void;
}

export type NewLootBoxType = {
    name: string;
    description: string;
    size: number;
    minBaseScore: number;
    maxBaseScore: number;
};

const newLBInitialState = { name: '', description: '', size: 0, minBaseScore: 0, maxBaseScore: 0 };
const newGuaranteeInitialState = { count: 0, min_score: 0, max_score: 0 };
const newWeightInitialState = { min_score: 0, max_score: 0, ipr: 0 };

const LootBoxTypeConfig = ({ onTypeSaved }: LootBoxTypeConfigProps) => {
    const [weights, setWeights] = useState<Weight[]>([]);
    const [guarantees, setGuarantees] = useState<Guarantee[]>([]);
    const [newLootBox, setNewLootBox] = useState<NewLootBoxType>(newLBInitialState);
    const [newGuarantee, setNewGuarantee] = useState<Guarantee>(newGuaranteeInitialState);
    const [newWeight, setNewWeight] = useState<Weight>(newWeightInitialState);
    const dispatch = useAppDispatch();

    const addConfigGroupOptions = (type) => {
        const { min_score, max_score, ipr } = newWeight;
        const { count, min_score: min, max_score: max } = newGuarantee;
        switch (type) {
            case 'weight':
                weights.push({ min_score: Number(min_score), max_score, ipr });
                setWeights([...weights]);
                setNewWeight(newWeightInitialState);
                break;
            case 'guarantee':
                guarantees.push({ count, min_score: min, max_score: max });
                setGuarantees([...guarantees]);
                setNewGuarantee(newGuaranteeInitialState);
                break;
        }
    };
    const deleteConfigGroupOption = (index: number, type) => {
        switch (type) {
            case 'weight':
                if (index > -1) {
                    weights.splice(index, 1);
                }
                setWeights([...weights]);
                break;
            case 'guarantee':
                if (index > -1) {
                    guarantees.splice(index, 1);
                }
                setGuarantees([...guarantees]);
                break;
        }
    };

    const handleChange = (e, name, type): void => {
        const { value } = e.target;
        switch (type) {
            case 'general':
                setNewLootBox({ ...newLootBox, [name]: value });
                break;
            case 'weight':
                setNewWeight({ ...newWeight, [name]: parseInt(value) });
                break;
            case 'guarantee':
                setNewGuarantee({ ...newGuarantee, [name]: parseInt(value) });
                break;
        }
    };

    const handleSave = (): void => {
        const lootBoxType: LootBoxType = {
            name: newLootBox.name,
            description: newLootBox.description,
            config: {
                size: Number(newLootBox.size),
                min_base_score: Number(newLootBox.minBaseScore),
                max_base_score: Number(newLootBox.maxBaseScore),
                guarantees,
                weights,
            },
        };
        apiFactory()
            .call(Admin.LootBoxes.newType, { value: lootBoxType })
            .then((r) => {
                if (r.ok) {
                    dispatch(addNotification({ message: 'Type Saved!', severity: 'success' }));
                    onTypeSaved();
                } else {
                    dispatch(addNotification({ message: r.error.detail || 'Error saving type', severity: 'error' }));
                }
            })
            .catch((e) => {
                dispatch(addNotification({ message: e.error, severity: 'error' }));
            });
    };

    return (
        <div className="LootBoxes">
            <StyledForm className="type-builder">
                <Typography type="headline" size={20}>
                    General Info:
                </Typography>
                <br />
                <div className="info">
                    <div className="info-block">
                        <label htmlFor="lb-name" title="The name of this loot box type">
                            Name:
                        </label>
                        <input onChange={(e) => handleChange(e, 'name', 'general')} type="text" name="lb-name" />

                        <label htmlFor="lb-desc" title="A more detailed description of the loot box type">
                            Description:
                        </label>
                        <textarea onChange={(e) => handleChange(e, 'description', 'general')} name="lb-desc" />
                    </div>
                    <div className="info-block">
                        <Typography type="headline" size={18} title="The loot box type's configuration parameters">
                            Config
                        </Typography>
                        <ConfigInput
                            label="Size:"
                            labelTitle="The number of Yats in the loot box"
                            name="size"
                            handleChange={handleChange}
                            newLootBox={newLootBox}
                        />
                        <ConfigInput
                            label="Minimum Base Score:"
                            labelTitle="The lower bound (inclusive) rhythm score for standard Yats in the loot box"
                            name="minBaseScore"
                            newLootBox={newLootBox}
                            handleChange={handleChange}
                        />
                        <ConfigInput
                            label="Maximum Base Score:"
                            labelTitle="The upper bound (exclusive) rhythm score for standard Yats in the loot box"
                            name="maxBaseScore"
                            handleChange={handleChange}
                            newLootBox={newLootBox}
                        />
                    </div>
                </div>
                <div className="config-items">
                    <GuaranteeConfig
                        deleteConfigGroupOption={deleteConfigGroupOption}
                        addConfigGroupOptions={addConfigGroupOptions}
                        handleChange={handleChange}
                        guarantees={guarantees}
                        newGuarantee={newGuarantee}
                    />
                    <WeightConfig
                        weights={weights}
                        handleChange={handleChange}
                        addConfigGroupOptions={addConfigGroupOptions}
                        deleteConfigGroupOption={deleteConfigGroupOption}
                        newWeight={newWeight}
                    />
                </div>
                <Wrapper width="100px">
                    <Button onClick={() => handleSave()} type={'button'} size="small">
                        Save Type
                    </Button>
                </Wrapper>
            </StyledForm>
        </div>
    );
};

export default LootBoxTypeConfig;
